import React from 'react';
import UnifiedSimpleConversionExplanation from './UnifiedSimpleConversionExplanation';

const PowerConversionExplanation = ({
  fromUnit,
  toUnit,
  inputValue,
  result,
  darkMode
}) => {
  const getConversionData = () => {
    // Watts to other units
    if (fromUnit === 'Watts' && toUnit === 'Kilowatts') {
      return {
        formula: 'kW = W ÷ 1000',
        conversionSteps: [
          `Take the power in watts: ${inputValue}W`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}kW`
        ],
        realWorldExample: "A typical microwave oven uses 1000 watts (1 kilowatt) of power.",
        funFacts: [
          "The watt is named after James Watt, inventor of the steam engine.",
          "A typical household in the US uses about 30 kilowatt-hours per day.",
          "Modern solar panels typically generate 250-400 watts each.",
        ]
      };
    }

    if (fromUnit === 'Watts' && toUnit === 'Horsepower (Mechanical)') {
      return {
        formula: 'hp = W × 0.001341',
        conversionSteps: [
          `Take the power in watts: ${inputValue}W`,
          `Multiply by 0.001341: ${inputValue} × 0.001341 = ${result}hp`
        ],
        realWorldExample: "A typical car engine produces between 150-200 horsepower (112-149 kilowatts).",
        funFacts: [
          "One horsepower was defined as the power needed to lift 550 pounds by 1 foot in 1 second.",
          "James Watt defined horsepower to compare steam engines to horses.",
          "A healthy human can sustain about 0.1 horsepower indefinitely.",
        ]
      };
    }

    // Kilowatts to other units
    if (fromUnit === 'Kilowatts' && toUnit === 'BTU/hour') {
      return {
        formula: 'BTU/h = kW × 3412.14',
        conversionSteps: [
          `Take the power in kilowatts: ${inputValue}kW`,
          `Multiply by 3412.14: ${inputValue} × 3412.14 = ${result}BTU/h`
        ],
        realWorldExample: "A 2.5kW air conditioner produces about 8,530 BTU/hour of cooling.",
        funFacts: [
          "BTU (British Thermal Unit) is still commonly used in HVAC systems.",
          "One BTU is the energy needed to raise one pound of water by 1°F.",
          "Modern air conditioners are rated in both BTU/h and kilowatts.",
        ]
      };
    }

    if (fromUnit === 'Horsepower (Mechanical)' && toUnit === 'Kilowatts') {
      return {
        formula: 'kW = hp × 0.7457',
        conversionSteps: [
          `Take the power in horsepower: ${inputValue}hp`,
          `Multiply by 0.7457: ${inputValue} × 0.7457 = ${result}kW`
        ],
        realWorldExample: "A 100hp engine produces about 74.57 kilowatts of power.",
        funFacts: [
          "Electric motors are often rated in both horsepower and kilowatts.",
          "The metric horsepower (PS) is slightly different from mechanical horsepower.",
          "Modern electric vehicles can produce over 1,000 horsepower.",
        ]
      };
    }

    // Default case for other conversions
    return {
      formula: `Converting from ${fromUnit} to ${toUnit}`,
      conversionSteps: [
        `Start with ${inputValue} ${fromUnit}`,
        `Apply conversion factor`,
        `Result: ${result} ${toUnit}`
      ],
      realWorldExample: "Power conversions are essential in engineering, automotive, and energy sectors.",
      funFacts: [
        "Power is the rate at which energy is transferred or converted.",
        "The watt is the standard SI unit of power.",
        "Different industries traditionally use different power units.",
      ]
    };
  };

  const conversionData = getConversionData();

  return (
    <UnifiedSimpleConversionExplanation
      conversionType="power"
      fromUnit={fromUnit}
      toUnit={toUnit}
      inputValue={inputValue}
      result={result}
      darkMode={darkMode}
      {...conversionData}
    />
  );
};

export default PowerConversionExplanation;
